import { useState } from "react";

import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import { Button } from "@mui/material";

import { ClientDetailResponse } from "@/api";
import { AmbulanzCreationDialog } from "@/common/components/ambulanzes/ambulanz-creation/AmbulanzCreationDialog";
import { ClientAppointmentCreationDialog } from "@/common/components/appointments/client-appointment-creation/ClientAppointmentCreationDialog";
import { AssignmentCreationDialog } from "@/common/components/assignments/assignment-creation/AssignmentCreationDialog";
import { EmailSendDialog } from "@/pages/clients/client/components/tabs/notes-tab/components/email-send/EmailSendDialog";
import { SmsSendDialog } from "@/pages/clients/client/components/tabs/notes-tab/components/sms-send/SmsSendDialog";
import { useTranslation } from "@/translations";

import { useClientCallbackActions } from "./hooks/useClientCallbackActions";

interface Props {
  client: ClientDetailResponse;
}

export function ClientMasterDataActionButtons({ client }: Props) {
  const { t } = useTranslation();
  const { handleCallbackAmbulanz, handleCallbackAssignment, isCallbackAmbulanzPending, isCallbackAssignmentPending } =
    useClientCallbackActions(client);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [isSmsSendDialogOpen, setIsSmsSendDialogOpen] = useState(false);
  const [isEmailSendDialogOpen, setIsEmailSendDialogOpen] = useState(false);
  const [isAppointmentCreationDialogOpen, setIsAppointmentCreationDialogOpen] = useState(false);
  const [isAmbulanzCreationDialogOpen, setIsAmbulanzCreationDialogOpen] = useState(false);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleCallbackAmbulanz}
        disabled={isCallbackAmbulanzPending}
        startIcon={<SupportAgentRoundedIcon />}
      >
        {t("clients.client.masterData.callbackAmbulanz")}
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setIsAmbulanzCreationDialogOpen(true)}
        startIcon={<ConfirmationNumberRoundedIcon />}
      >
        {t("ambulanzes.create")}
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={handleCallbackAssignment}
        disabled={isCallbackAssignmentPending}
        startIcon={<ChecklistRoundedIcon />}
      >
        {t("clients.client.masterData.callbackAssignment")}
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setIsAssignmentDialogOpen(true)}
        startIcon={<AddTaskRoundedIcon />}
      >
        {t("clients.client.masterData.createAssignment")}
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setIsSmsSendDialogOpen(true)}
        startIcon={<SmsRoundedIcon />}
      >
        {t("clients.client.sms.sendSms")}
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setIsEmailSendDialogOpen(true)}
        startIcon={<MailOutlineRoundedIcon />}
      >
        {t("clients.client.email.sendEmail")}
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setIsAppointmentCreationDialogOpen(true)}
        startIcon={<EventRoundedIcon />}
      >
        {t("clients.client.appointmentCreation.createAppointment")}
      </Button>
      {isAssignmentDialogOpen && (
        <AssignmentCreationDialog onClose={() => setIsAssignmentDialogOpen(false)} client={client} />
      )}
      {isSmsSendDialogOpen && (
        <SmsSendDialog clientId={client.clientId} onClose={() => setIsSmsSendDialogOpen(false)} />
      )}
      {isEmailSendDialogOpen && <EmailSendDialog client={client} onClose={() => setIsEmailSendDialogOpen(false)} />}
      {isAppointmentCreationDialogOpen && (
        <ClientAppointmentCreationDialog client={client} onClose={() => setIsAppointmentCreationDialogOpen(false)} />
      )}
      {isAmbulanzCreationDialogOpen && (
        <AmbulanzCreationDialog client={client} onClose={() => setIsAmbulanzCreationDialogOpen(false)} />
      )}
    </>
  );
}
